import React from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
} from 'recharts';
import { useIotSenzorTeplotaQuery } from '../../api/JsdhNotifikaceService/service/IotMutations';

interface TeplotaChartProps {
    senzorId: number;
    /** Pocet posledních vzorku (BE parametr `limit`), ne pocet hodin. */
    limit?: number;
}

const formatTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
};

const TeplotaChart: React.FC<TeplotaChartProps> = ({ senzorId, limit = 288 }) => {
    const { data, isLoading, error } = useIotSenzorTeplotaQuery(senzorId, limit);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Chyba při načítání dat: {error.message}</Alert>;
    }

    if (!data?.mereni?.length) {
        return (
            <Alert severity="info">
                Žádná teplotní data za posledních 24 hodin.
            </Alert>
        );
    }

    // Připravit data pro graf
    // BE vrací mereni v pořadí ORDER BY cas DESC (nejnovější první) → pro graf
    // potřebujeme chronologické pořadí (nejnovější vpravo), proto reverse na kopii pole.
    const chartData = [...data.mereni].reverse().map((m) => ({
        cas: m.cas,
        teplota: m.teplota,
        label: formatTime(m.cas),
    }));

    // Najít min/max pro osu Y
    const temps = chartData.map((d) => d.teplota);
    const minTemp = Math.floor(Math.min(...temps) - 2);
    const maxTemp = Math.ceil(Math.max(...temps) + 2);

    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom>
                Průběh teploty za posledních 24 hodin
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12 }}
                        interval="preserveStartEnd"
                    />
                    <YAxis
                        domain={[minTemp, maxTemp]}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `${value}°C`}
                    />
                    <Tooltip
                        formatter={(value: number) => [`${value}°C`, 'Teplota']}
                        labelFormatter={(label, payload) => {
                            if (payload?.[0]?.payload?.cas) {
                                const date = new Date(payload[0].payload.cas);
                                return date.toLocaleString('cs-CZ');
                            }
                            return label;
                        }}
                    />
                    {/* Referenční čára pro vysokou teplotu */}
                    <ReferenceLine
                        y={50}
                        stroke="red"
                        strokeDasharray="5 5"
                        label={{ value: '50°C', position: 'right', fill: 'red', fontSize: 10 }}
                    />
                    <Line
                        type="monotone"
                        dataKey="teplota"
                        stroke="#1976d2"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 6 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default TeplotaChart;
